import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';
import { getAuthUser } from 'store/auth/selectors';
import {
  A11Y_FOCUS_STRONG,
  A11Y_HIGH_CONTRAST,
  A11Y_REDUCE_MOTION,
  A11Y_TEXT_SCALE,
  A11Y_TEXT_SPACING,
  A11Y_UNDERLINE_LINKS,
  A11yPreferences,
  A11yReduceMotion,
  A11yTextScale,
  A11yTextSpacing,
  applyA11yPreferences,
  DEFAULT_A11Y_PREFERENCES,
  normalizeA11yPreferences,
} from 'utils/apply-a11y-preferences';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import {
  PwaSection,
  PwaSegmented,
  PwaSwitch,
  PwaTopBar,
} from './ui';

export const PwaAccessibilityPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector(getAuthUser);
  const preferences = usePreferences();
  const userUuid = currentUser?.userUuid ?? '';

  const readPrefs = (): A11yPreferences => {
    if (!userUuid) return DEFAULT_A11Y_PREFERENCES;
    return normalizeA11yPreferences({
      [A11Y_TEXT_SCALE]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_TEXT_SCALE },
        userUuid
      ),
      [A11Y_TEXT_SPACING]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_TEXT_SPACING },
        userUuid
      ),
      [A11Y_HIGH_CONTRAST]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_HIGH_CONTRAST },
        userUuid
      ),
      [A11Y_UNDERLINE_LINKS]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_UNDERLINE_LINKS },
        userUuid
      ),
      [A11Y_REDUCE_MOTION]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_REDUCE_MOTION },
        userUuid
      ),
      [A11Y_FOCUS_STRONG]: preferences(
        PreferenceActions.GET,
        PreferenceSections.APP,
        { name: A11Y_FOCUS_STRONG },
        userUuid
      ),
    });
  };

  const [a11yPrefs, setA11yPrefs] = React.useState<A11yPreferences>(
    DEFAULT_A11Y_PREFERENCES
  );

  React.useEffect(() => {
    const next = readPrefs();
    setA11yPrefs(next);
    applyA11yPreferences(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from persisted prefs only
  }, [userUuid, currentUser?.prefDetails?.[1]]);

  const savePreference = (name: string, value: unknown) => {
    if (!userUuid) return;
    preferences(
      PreferenceActions.SET,
      PreferenceSections.APP,
      { name, value },
      userUuid
    );
  };

  const updatePrefs = (partial: Partial<A11yPreferences>) => {
    const next = { ...a11yPrefs, ...partial };
    setA11yPrefs(next);
    applyA11yPreferences(next);
  };

  const textSizeValue =
    a11yPrefs.textScale >= 1.25
      ? 'larger'
      : a11yPrefs.textScale >= 1.125
        ? 'large'
        : 'default';

  const onTextSize = (value: string) => {
    const scale: A11yTextScale =
      value === 'larger' ? 1.25 : value === 'large' ? 1.125 : 1;
    updatePrefs({ textScale: scale });
    savePreference(A11Y_TEXT_SCALE, scale);
  };

  const onSpacing = (value: string) => {
    const spacing: A11yTextSpacing =
      value === 'relaxed' ? 'relaxed' : 'default';
    updatePrefs({ textSpacing: spacing });
    savePreference(A11Y_TEXT_SPACING, spacing);
  };

  const onMotion = (value: string) => {
    const motion: A11yReduceMotion =
      value === 'system' || value === 'on' || value === 'off' ? value : 'off';
    updatePrefs({ reduceMotion: motion });
    savePreference(A11Y_REDUCE_MOTION, motion);
  };

  const Toggle = ({
    checked,
    onChange,
    title,
    sub,
  }: {
    checked: boolean;
    onChange: (next: boolean) => void;
    title: string;
    sub: string;
  }) => (
    <div className="pwa-row static">
      <span className="pwa-row-body">
        <span className="pwa-row-title">{title}</span>
        <span className="pwa-row-sub wrap">{sub}</span>
      </span>
      <PwaSwitch checked={checked} onChange={onChange} label={title} />
    </div>
  );

  return (
    <div className="pwa-more-page" data-shell="pwa">
      <PwaTopBar
        title={t('navigation.accessibility')}
        onBack={() => navigate(routes.account.root)}
        backLabel={t('common.back')}
      />
      <main className="pwa-main">
        <div className="pwa-largetitle" style={{ paddingBottom: 8 }}>
          <p style={{ marginTop: 0 }}>
            {t('accessibility.settings.page-intro')}
          </p>
        </div>
        <PwaSection title={t('accessibility.settings.section-text')}>
          <div className="pwa-list">
            <div className="pwa-row-block">
              <span className="pwa-row-title">
                {t('accessibility.settings.text-size-label')}
              </span>
              <PwaSegmented
                label={t('accessibility.settings.text-size-label')}
                value={textSizeValue}
                onChange={onTextSize}
                options={[
                  {
                    value: 'default',
                    label: t('accessibility.settings.text-size-default'),
                  },
                  {
                    value: 'large',
                    label: t('accessibility.settings.text-size-large'),
                  },
                  {
                    value: 'larger',
                    label: t('accessibility.settings.text-size-larger'),
                  },
                ]}
              />
              <span className="pwa-row-title" style={{ marginTop: 4 }}>
                {t('accessibility.settings.text-spacing-label')}
              </span>
              <PwaSegmented
                label={t('accessibility.settings.text-spacing-label')}
                value={a11yPrefs.textSpacing}
                onChange={onSpacing}
                options={[
                  {
                    value: 'default',
                    label: t('accessibility.settings.text-spacing-default'),
                  },
                  {
                    value: 'relaxed',
                    label: t('accessibility.settings.text-spacing-relaxed'),
                  },
                ]}
              />
              <div className="pwa-aa-preview">
                {t('accessibility.settings.preview', {
                  defaultValue:
                    'Preview: 5 documents are ready to review for My Practice 1.',
                })}
              </div>
            </div>
          </div>
        </PwaSection>
        <PwaSection title={t('accessibility.settings.section-contrast')}>
          <div className="pwa-list">
            <Toggle
              checked={a11yPrefs.highContrast}
              onChange={(checked) => {
                updatePrefs({ highContrast: checked });
                savePreference(A11Y_HIGH_CONTRAST, checked);
              }}
              title={t('accessibility.settings.high-contrast-label')}
              sub={t('accessibility.settings.high-contrast-help')}
            />
            <Toggle
              checked={a11yPrefs.underlineLinks}
              onChange={(checked) => {
                updatePrefs({ underlineLinks: checked });
                savePreference(A11Y_UNDERLINE_LINKS, checked);
              }}
              title={t('accessibility.settings.underline-links-label')}
              sub={t('accessibility.settings.underline-links-help')}
            />
            <Toggle
              checked={a11yPrefs.focusStrong}
              onChange={(checked) => {
                updatePrefs({ focusStrong: checked });
                savePreference(A11Y_FOCUS_STRONG, checked);
              }}
              title={t('accessibility.settings.focus-strong-label')}
              sub={t('accessibility.settings.focus-strong-help')}
            />
          </div>
        </PwaSection>
        <PwaSection title={t('accessibility.settings.section-motion')}>
          <div className="pwa-list">
            <div className="pwa-row-block">
              <span className="pwa-row-sub wrap">
                {t('accessibility.settings.section-motion-help')}
              </span>
              <PwaSegmented
                label={t('accessibility.settings.reduce-motion-label')}
                value={a11yPrefs.reduceMotion}
                onChange={onMotion}
                options={[
                  {
                    value: 'system',
                    label: t('accessibility.settings.reduce-motion-system'),
                  },
                  {
                    value: 'on',
                    label: t('accessibility.settings.reduce-motion-on', {
                      defaultValue: 'Reduce',
                    }),
                  },
                  {
                    value: 'off',
                    label: t('accessibility.settings.reduce-motion-off', {
                      defaultValue: 'Full',
                    }),
                  },
                ]}
              />
            </div>
          </div>
        </PwaSection>
      </main>
    </div>
  );
};

export default PwaAccessibilityPage;
